import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { Star, TrendingUp, Users, Award, MessageCircle } from 'lucide-react';
import { API_URL } from './config';

const RatingAnalytics = () => {
  const [ratingAnalytics, setRatingAnalytics] = useState(null);
  const [topDrivers, setTopDrivers] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [recentFeedback, setRecentFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRatingData();
  }, []);

  const fetchRatingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch rating analytics
      const analyticsResponse = await fetch(`${API_URL}/rating/analytics`);
      const analyticsData = await analyticsResponse.json();

      // Fetch top drivers
      const driversResponse = await fetch(`${API_URL}/rating/top-drivers?limit=10`);
      const driversData = await driversResponse.json();

      // Fetch top customers
      const customersResponse = await fetch(`${API_URL}/rating/top-customers?limit=10`);
      const customersData = await customersResponse.json();

      // Fetch recent feedback
      const feedbackResponse = await fetch(`${API_URL}/rating/recent-feedback?limit=20`);
      const feedbackData = await feedbackResponse.json();

      if (analyticsData.success) {
        setRatingAnalytics(analyticsData.data);
      }

      if (driversData.success) {
        setTopDrivers(driversData.data);
      }

      if (customersData.success) {
        setTopCustomers(customersData.data);
      }

      if (feedbackData.success) {
        setRecentFeedback(feedbackData.data);
      }

    } catch (err) {
      setError('Failed to fetch rating analytics');
      console.error('Rating analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} size={16} fill="#ffc000" color="#ffc000" />);
    }

    if (hasHalfStar && fullStars < 5) {
      stars.push(<Star key="half" size={16} fill="#ffc000" color="#ffc000" opacity={0.7} />);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} size={16} color="#ddd" />);
    }

    return stars;
  };

  const getRatingBadgeColor = (rating) => {
    if (rating >= 4.5) return 'success';
    if (rating >= 3.5) return 'primary';
    if (rating >= 2.5) return 'warning';
    return 'danger';
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading rating analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {error}
      </Alert>
    );
  }

  return (
    <div className="rating-analytics">
      {/* Overview Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <Users className="text-primary" size={24} />
              </div>
              <h3>{ratingAnalytics?.total_rated_drivers || 0}</h3>
              <p className="text-muted mb-0">Rated Drivers</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <Users className="text-info" size={24} />
              </div>
              <h3>{ratingAnalytics?.total_rated_customers || 0}</h3>
              <p className="text-muted mb-0">Rated Customers</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <Star className="text-warning" size={24} />
              </div>
              <h3>{parseFloat(ratingAnalytics?.avg_driver_rating || 0).toFixed(2)}</h3>
              <p className="text-muted mb-0">Avg Driver Rating</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <Star className="text-success" size={24} />
              </div>
              <h3>{parseFloat(ratingAnalytics?.avg_customer_rating || 0).toFixed(2)}</h3>
              <p className="text-muted mb-0">Avg Customer Rating</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top Rated Drivers */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex align-items-center">
            <Award className="text-warning me-2" size={20} />
            <h5 className="mb-0">Top Rated Drivers</h5>
          </div>
        </Card.Header>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Driver</th>
                <th>Rating</th>
                <th>Total Ratings</th>
                <th>Vehicle</th>
                <th>Performance</th>
              </tr>
            </thead>
            <tbody>
              {topDrivers.map((driver, index) => (
                <tr key={driver.driverid}>
                  <td>
                    <Badge bg={index < 3 ? 'warning' : 'secondary'}>
                      #{index + 1}
                    </Badge>
                  </td>
                  <td>
                    <strong>{driver.firstname} {driver.surname}</strong>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      {renderStars(parseFloat(driver.rating || 0))}
                      <span className="ms-2">{parseFloat(driver.rating || 0).toFixed(2)}</span>
                    </div>
                    <div className="small text-muted mt-1">
                      <div>Private: {driver.private_average_rating_given || 0} ({driver.private_total_ratings_given || 0})</div>
                      <div>Rideshare: {driver.rideshare_driver_average_rating_given || 0} ({driver.rideshare_driver_total_ratings_given || 0})</div>
                    </div>
                  </td>
                  <td>{driver.total_ratings || 0}</td>
                  <td>
                    <small className="text-muted">
                      {driver.make} {driver.model} ({driver.colour})
                    </small>
                  </td>
                  <td>
                    <Badge bg={getRatingBadgeColor(parseFloat(driver.rating || 0))}>
                      {parseFloat(driver.rating || 0) >= 4.5 ? 'Excellent' :
                       parseFloat(driver.rating || 0) >= 3.5 ? 'Good' :
                       parseFloat(driver.rating || 0) >= 2.5 ? 'Average' : 'Needs Improvement'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Top Rated Customers */}
      <Card>
        <Card.Header>
          <div className="d-flex align-items-center">
            <Award className="text-success me-2" size={20} />
            <h5 className="mb-0">Top Rated Customers</h5>
          </div>
        </Card.Header>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Customer</th>
                <th>Rating</th>
                <th>Total Ratings</th>
                <th>Contact</th>
                <th>Performance</th>
              </tr>
            </thead>
            <tbody>
              {topCustomers.map((customer, index) => (
                <tr key={customer.customerid}>
                  <td>
                    <Badge bg={index < 3 ? 'success' : 'secondary'}>
                      #{index + 1}
                    </Badge>
                  </td>
                  <td>
                    <strong>{customer.firstname} {customer.surname}</strong>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      {renderStars(parseFloat(customer.rating || 0))}
                      <span className="ms-2">{parseFloat(customer.rating || 0).toFixed(2)}</span>
                    </div>
                    <div className="small text-muted mt-1">
                      <div>Private: {customer.private_average_rating_given || 0} ({customer.private_total_ratings_given || 0})</div>
                      <div>Rideshare: {customer.rideshare_passenger_average_rating_given || 0} ({customer.rideshare_passenger_total_ratings_given || 0})</div>
                    </div>
                  </td>
                  <td>{customer.total_ratings || 0}</td>
                  <td>
                    <small className="text-muted">
                      {customer.phone}
                      {customer.email && <><br />{customer.email}</>}
                    </small>
                  </td>
                  <td>
                    <Badge bg={getRatingBadgeColor(parseFloat(customer.rating || 0))}>
                      {parseFloat(customer.rating || 0) >= 4.5 ? 'Excellent' :
                       parseFloat(customer.rating || 0) >= 3.5 ? 'Good' :
                       parseFloat(customer.rating || 0) >= 2.5 ? 'Average' : 'Needs Improvement'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Recent Feedback Section */}
      <Card>
        <Card.Header>
          <div className="d-flex align-items-center">
            <MessageCircle className="text-info me-2" size={20} />
            <h5 className="mb-0">Recent Feedback</h5>
          </div>
        </Card.Header>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>User</th>
                <th>Rating</th>
                <th>Trip Type</th>
                <th>Feedback</th>
              </tr>
            </thead>
            <tbody>
              {recentFeedback && recentFeedback.length > 0 ? recentFeedback.map((feedback, index) => (
                <tr key={index}>
                  <td>
                    <small>
                      {new Date(feedback.feedback_date).toLocaleDateString()}
                      <br />
                      <span className="text-muted">
                        {new Date(feedback.feedback_date).toLocaleTimeString()}
                      </span>
                    </small>
                  </td>
                  <td>
                    <Badge 
                      bg={feedback.rating_direction === 'customer_to_driver' ? 'primary' : 'success'}
                      className="text-white"
                    >
                      {feedback.rating_direction === 'customer_to_driver' ? 'Customer → Driver' : 'Driver → Customer'}
                    </Badge>
                  </td>
                  <td>
                    {feedback.rating_direction === 'customer_to_driver' ? (
                      <div>
                        <strong>{feedback.rated_for?.name || 'Unknown Driver'}</strong>
                        {feedback.rated_for?.name && (
                          <small className="text-muted d-block">
                            Driver ID: {feedback.rated_for?.id}
                          </small>
                        )}
                        <br />
                        <small className="text-muted">
                          From: {feedback.rated_by?.name || 'Anonymous Customer'}
                        </small>
                      </div>
                    ) : (
                      <div>
                        <strong>{feedback.rated_for?.name || 'Unknown Customer'}</strong>
                        <br />
                        <small className="text-muted">
                          From: {feedback.rated_by?.name || 'Anonymous Driver'}
                        </small>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      {renderStars(parseFloat(feedback.rating || 0))}
                      <span className="ms-2">{parseFloat(feedback.rating || 0).toFixed(1)}</span>
                    </div>
                  </td>
                  <td>
                    <Badge bg={feedback.trip_type === 'private_trip' ? 'warning' : 'info'}>
                      {feedback.trip_type === 'private_trip' ? 'Private' : 'Rideshare'}
                    </Badge>
                  </td>
                  <td>
                    <div className="feedback-text" style={{ maxWidth: '300px' }}>
                      <em>"{feedback.feedback || 'No comment provided'}"</em>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    <small className="text-muted">No feedback data available</small>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

// Add custom styles
const styles = `
  .feedback-text {
    font-style: italic;
    color: #666;
    line-height: 1.4;
  }
`;

export default RatingAnalytics;
